import { useCallback, useEffect, useRef, useState } from 'react';

import { apiErrorMessage, fetchQuote, isPricingUnavailable, type Quote } from '../lib/api';

/**
 * The server's price for the current configuration.
 *
 * THE BROWSER DOES NOT PRICE ANYTHING. When custom hashrates became purchasable there
 * were two ways to show their price: publish the per-TH rates and multiply here, or ask.
 * This asks. A multiplication in TypeScript would be a second pricing implementation,
 * and the day it disagreed with the server -- a rounding rule, a stale bracket boundary,
 * a price list changed between deploys -- a customer would be shown one number and
 * charged another. Asking cannot drift.
 *
 * The race this guards is the same one useOdds guards, for the same reason: a customer
 * dragging the slider from 100 to 600 to 250 issues three requests that may return in
 * any order. A late answer for 600 landing after the answer for 250 would put the wrong
 * price under the right hashrate. Two defences, because either alone leaves a gap:
 *
 *   1. A monotonically increasing request id -- only the newest response may write.
 *   2. An AbortController per request, so superseded ones are cancelled in flight.
 *
 * Previous figures are cleared the moment inputs change, so a stale price is never shown
 * beside a configuration it does not belong to. A price is either current or absent.
 */

export type QuoteState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; quote: Quote }
  /** The server can price the range, but not this value. A commercial gap, stated plainly. */
  | { status: 'unpriceable'; reason: string }
  /** Something else went wrong -- network, server error. Not a statement about the price. */
  | { status: 'error'; reason: string };

export function useQuote(params: {
  hashrateTh: number | null;
  termDays: number;
  discountCode?: string | null;
}): { state: QuoteState; retry: () => void } {
  const { hashrateTh, termDays, discountCode = null } = params;

  const [state, setState] = useState<QuoteState>({ status: 'idle' });
  const [attempt, setAttempt] = useState(0);

  const latestRequestId = useRef(0);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  useEffect(() => {
    if (hashrateTh == null || hashrateTh <= 0) {
      setState({ status: 'idle' });
      return undefined;
    }

    const requestId = latestRequestId.current + 1;
    latestRequestId.current = requestId;

    const controller = new AbortController();
    setState({ status: 'loading' });

    fetchQuote({ hashrateTh, termDays, discountCode }, controller.signal)
      .then((quote) => {
        if (requestId !== latestRequestId.current) return;
        setState({ status: 'ready', quote });
      })
      .catch((error: unknown) => {
        if (requestId !== latestRequestId.current) return;
        if (controller.signal.aborted) return;

        // A configuration the server will not price is a different thing from a failure
        // to reach the server, and the UI says so differently. Neither is filled in with
        // a guess.
        setState(
          isPricingUnavailable(error)
            ? { status: 'unpriceable', reason: apiErrorMessage(error, 'That amount is not on sale.') }
            : { status: 'error', reason: apiErrorMessage(error, 'Could not load a price just now.') },
        );
      });

    return () => controller.abort();
    // The discount code is part of the configuration being priced: applying or removing
    // one re-quotes exactly like changing the hashrate does, and the same race guard
    // covers it.
  }, [hashrateTh, termDays, discountCode, attempt]);

  return { state, retry };
}
